import React, { useEffect, useState } from 'react';
import trainService from '../services/trainService';
import { formatCurrency } from '../utils/helpers';
import stationList from '../utils/stationsList';

const getInitialFormData = () => ({
  trainName: '',
  trainNumber: '',
  source: '',
  destination: '',
  departureTime: '',
  arrivalTime: '',
  baseFare: '',
  coaches: '10',
  seatsPerCoach: '72',
});

const ManageTrainsPage = () => {
  const [trains, setTrains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTrain, setEditingTrain] = useState(null);
  const [formData, setFormData] = useState(getInitialFormData);

  const stations = stationList;

  useEffect(() => {
    const fetchTrains = async () => {
      try {
        const allTrains = await trainService.getAllTrains();
        setTrains(allTrains);
      } catch (error) {
        console.error('Error fetching trains:', error);
        alert('Error loading trains');
      } finally {
        setLoading(false);
      }
    };

    fetchTrains();
  }, []);

  const refreshTrains = async () => {
    const allTrains = await trainService.getAllTrains();
    setTrains(allTrains);
  };

  const resetForm = () => {
    setFormData(getInitialFormData());
    setEditingTrain(null);
    setShowForm(false);
  };

  const handleToggleForm = () => {
    if (showForm) {
      resetForm();
      return;
    }

    setFormData(getInitialFormData());
    setEditingTrain(null);
    setShowForm(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(currentData => ({ ...currentData, [name]: value }));
  };

  const handleEditTrain = (train) => {
    setEditingTrain(train);
    setShowForm(true);
    setFormData({
      trainName: train.trainName || '',
      trainNumber: train.trainNumber || '',
      source: train.source || '',
      destination: train.destination || '',
      departureTime: train.departureTime || '',
      arrivalTime: train.arrivalTime || '',
      baseFare: `${train.baseFare ?? ''}`,
      coaches: `${train.totalCoaches ?? 10}`,
      seatsPerCoach: `${train.seats?.[0]?.totalSeats ?? 72}`,
    });
  };

  const handleSubmitTrain = async (e) => {
    e.preventDefault();

    if (!formData.trainName || !formData.source || !formData.destination) {
      alert('Please fill all required fields');
      return;
    }

    const trainPayload = {
      trainName: formData.trainName,
      trainNumber: formData.trainNumber,
      source: formData.source,
      destination: formData.destination,
      departureTime: formData.departureTime,
      arrivalTime: formData.arrivalTime,
      baseFare: parseInt(formData.baseFare, 10),
      route: [formData.source, formData.destination],
    };

    try {
      if (editingTrain) {
        await trainService.updateTrain(editingTrain.id, trainPayload);
        alert('Train updated successfully');
      } else {
        await trainService.addTrain({
          ...trainPayload,
          coaches: parseInt(formData.coaches, 10),
          seatsPerCoach: parseInt(formData.seatsPerCoach, 10),
          operatingDays: ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'],
        });
        alert('Train added successfully');
      }

      await refreshTrains();
      resetForm();
    } catch (error) {
      alert(`Error ${editingTrain ? 'updating' : 'adding'} train: ${error.message}`);
    }
  };

  const handleDeleteTrain = async (trainId) => {
    if (!window.confirm('Are you sure you want to delete this train?')) {
      return;
    }

    try {
      await trainService.deleteTrain(trainId);
      alert('Train deleted successfully');
      await refreshTrains();
    } catch (error) {
      alert('Error deleting train: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Manage Trains</h1>
          <button
            onClick={handleToggleForm}
            className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            {showForm ? 'Cancel' : 'Add New Train'}
          </button>
        </div>

        {showForm && (
          <div className="bg-white rounded-lg shadow-md p-8 mb-8">
            <h2 className="text-2xl font-bold mb-6">
              {editingTrain ? `Edit ${editingTrain.trainName}` : 'Add New Train'}
            </h2>
            <form onSubmit={handleSubmitTrain} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <input
                type="text"
                name="trainName"
                value={formData.trainName}
                onChange={handleInputChange}
                placeholder="Train Name"
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                required
              />
              <input
                type="text"
                name="trainNumber"
                value={formData.trainNumber}
                onChange={handleInputChange}
                placeholder="Train Number"
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
              />
              <select
                name="source"
                value={formData.source}
                onChange={handleInputChange}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                required
              >
                <option value="">Select Source</option>
                {stations.map((station) => <option key={station} value={station}>{station}</option>)}
              </select>
              <select
                name="destination"
                value={formData.destination}
                onChange={handleInputChange}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                required
              >
                <option value="">Select Destination</option>
                {stations.map((station) => <option key={station} value={station}>{station}</option>)}
              </select>
              <input
                type="datetime-local"
                name="departureTime"
                value={formData.departureTime}
                onChange={handleInputChange}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
              />
              <input
                type="datetime-local"
                name="arrivalTime"
                value={formData.arrivalTime}
                onChange={handleInputChange}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
              />
              <input
                type="number"
                name="baseFare"
                value={formData.baseFare}
                onChange={handleInputChange}
                placeholder="Base Fare"
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
              />
              <input
                type="number"
                name="coaches"
                value={formData.coaches}
                onChange={handleInputChange}
                placeholder="Number of Coaches"
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary disabled:bg-gray-100"
                disabled={Boolean(editingTrain)}
              />
              {editingTrain && (
                <p className="col-span-2 text-sm text-gray-600">
                  Coach capacity changes are disabled while editing existing trains to avoid breaking seat maps.
                </p>
              )}
              <button
                type="submit"
                className="col-span-2 bg-primary text-white py-2 rounded-lg hover:bg-blue-700 transition font-semibold"
              >
                {editingTrain ? 'Save Changes' : 'Add Train'}
              </button>
            </form>
          </div>
        )}

        <div className="space-y-4">
          {trains.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <p className="text-lg text-gray-600">No trains available</p>
            </div>
          ) : (
            trains.map((train) => (
              <div key={train.id} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold">{train.trainName}</h3>
                    <p className="text-sm text-gray-600">#{train.trainNumber}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditTrain(train)}
                      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteTrain(train.id)}
                      className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Route</p>
                    <p className="font-semibold">{train.source} {'->'} {train.destination}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Departure</p>
                    <p className="font-semibold">{train.departureTime}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Fare</p>
                    <p className="font-semibold">{formatCurrency(train.baseFare)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Availability</p>
                    <p className="font-semibold text-green-600">{train.availableSeats} seats</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageTrainsPage;
